import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchBanks,
  createInvoice,
} from "../../../../../../../../services/pos/Tables";
import { fetchCashiers } from "../../../../../../../../services/Settings/Cashier"; // Import fetchUserBanks function
import { useOrder } from "../../../../../../../../context/OrderCreator/TablesContext/OrderContext";
import PaymentDialog from "./PaymentDialog";
import { useInvoice } from "../../../../../../../../context/OrderCreator/TablesContext/InvoiceCreator";
import { handleBrowserPrint } from "../../../../../PrintTemplates/printUtils";
import { handleBrowserPrintAfterEnd } from "../../../../../PrintTemplates/PtintAfterEnd/printUtilsAfterEnd"; // NEW: استيراد دالة طباعة الفاتورة النهائية
import { printCustomerInvoice } from "../../../../../../../../services/pos/PrinterApi"; // NEW: استيراد دالة طباعة الفاتورة عبر API
import { getCountPrintOrder } from "../../../../../../../../services/Settings/printers"; // NEW: استيراد دالة جلب عدد مرات الطباعة
import { usePermissions } from "../../../../../../../../context/PermissionsAndFeatures/UserPermissionsContext";
import { useSettings } from "../../../../../../../../context/SettingsContext";


const InvoiceCreator = ({ orderId, onSuccess }) => {
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:900px)");
  const isLargeTablet = useMediaQuery("(min-width:900px) and (max-width:1200px)"); // اختياري لتحسين آيباد برو
  const controlSize = isTablet ? "medium" : "small";
  const {
    getInvoiceSettings,
    setInvoiceSettings,
    calculateTotalAmount,
    defaultSettings,
    calculateItemValue, // ✅ دالة لحساب قيمة فردية (قيمة ثابتة + نسبة مئوية)
  } = useInvoice();

  const invoiceSettings = getInvoiceSettings(orderId);

  // دالة wrapper عشان تبعت orderId
  const updateInvoiceSettings = (newSettings) => {
    setInvoiceSettings(orderId, newSettings);
  };

  // derive lock flags (support both new and legacy flag names)
  const serviceLocked =
    invoiceSettings?.is_service_required || invoiceSettings?.service_required;
  const discountLocked =
    invoiceSettings?.is_discount_required || invoiceSettings?.discount_required;
  const taxLocked =
    invoiceSettings?.is_tax_required || invoiceSettings?.tax_required;

  // tax as percentage flag
  const taxIsPercentage = !!invoiceSettings?.tax_is_percentage;

  const [banks, setBanks] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [payments, setPayments] = useState([
    { bank_id: "", amount: "", payment_method: "cash" },
  ]);
  const [showServiceFields, setShowServiceFields] = useState(false);
  const [showDiscountFields, setShowDiscountFields] = useState(false);
  const [showTaxFields, setShowTaxFields] = useState(false);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const { orderDetails } = useOrder();
  const [loadingOrder, setLoadingOrder] = useState(false);
  const { loadOrder } = useOrder();
  const { hasPermission } = usePermissions();
  // جلب الإعدادات من SettingsProvider
  const { settings, loading: settingsLoading, error: settingsError } = useSettings();


  // ⬅️ داخل InvoiceCreator.js
  useEffect(() => {
    if (orderId && defaultSettings) {
      const subTotal = orderDetails?.details?.total || orderDetails?.total || 0;

      const updated = {
        ...defaultSettings, // القيم من الـ API (tax, service, delivery)
        ...invoiceSettings, // أي قيم سابقة للفاتورة دي
        sub_total: subTotal, // اجمالي الطلب
      };

      updated.total_amount = calculateTotalAmount(updated);

      setInvoiceSettings(orderId, updated);
    }
  }, [orderId, defaultSettings, orderDetails]);

  useEffect(() => {
    if (
      invoiceSettings.total_amount &&
      payments.length === 1 &&
      !payments[0].amount
    ) {
      setPayments([{ ...payments[0], amount: invoiceSettings.total_amount }]);
    }
  }, [invoiceSettings.total_amount]);

  useEffect(() => {
    const handleOrderUpdate = (e) => {
      if (e.detail.orderId === orderId) {
        // ✨ استخدم الـ Context بدل من الطلب المباشر
        loadOrder(orderId);
      }
    };

    window.addEventListener("orderUpdated", handleOrderUpdate);
    return () => window.removeEventListener("orderUpdated", handleOrderUpdate);
  }, [orderId, loadOrder]);

  useEffect(() => {
    const loadBanksAndCashiers = async () => {
      try {
        const banksData = await fetchBanks();
        setBanks(banksData || []);

        const cashiersData = await fetchCashiers();
        setCashiers(cashiersData || []);
      } catch (err) {
        console.error("❌ فشل تحميل البنوك/الخزن:", err);
      }
    };

    loadBanksAndCashiers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...invoiceSettings, [name]: value };

    const relatedFields = [
      "service",
      "service_ratio",
      "tax",
      "tax_ratio",
      "discount",
      "discount_ratio",
      "sub_total",
    ];

    if (relatedFields.includes(name)) {
      updatedForm.total_amount = calculateTotalAmount(updatedForm);
    }

    updateInvoiceSettings(updatedForm); // ⬅️ لازم تحفظ التعديلات هنا
  };

  const handleCreateInvoice = async () => {
    try {
      const invoiceData = {
        order_id: orderId,
        total_amount: parseFloat(invoiceSettings.total_amount) || 0,
        order_status: "finished",
        root_id: parseInt(
          invoiceSettings.root_id || localStorage.getItem("branch_id")
        ),
        service: parseFloat(invoiceSettings.service) || 0,
        delivery_service: parseFloat(invoiceSettings.delivery_service) || 0,
        discount: parseFloat(invoiceSettings.discount) || 0,
        service_ratio: parseFloat(invoiceSettings.service_ratio) || 0,
        delivery_service_ratio:
          parseFloat(invoiceSettings.delivery_service_ratio) || 0,
        tax_ratio: parseFloat(invoiceSettings.tax_ratio) || 0,
        discount_ratio: parseFloat(invoiceSettings.discount_ratio) || 0,
        sub_total: parseFloat(invoiceSettings.sub_total) || 0,
        date: new Date().toISOString().split("T")[0],
        tax: parseFloat(invoiceSettings.tax) || 0,
        tax_is_percentage: invoiceSettings.tax_is_percentage ? 1 : 0,
        payments,
      };

      const response = await createInvoice(invoiceData);

      // ✅ بعد نجاح حفظ الفاتورة: طباعة الفاتورة النهائية (حسب طريقة الطباعة المحددة
      //       ومع مراعاة خيار print_invoice_after_saving)
      const printingMethod = (settings?.printing_method || "").toString().toLowerCase();
      const printAfterSaving = settings?.print_invoice_after_saving ?? 1; // افتراضي 1 (طباعة)
      // اعتبر "0" أو 0 كتعطيل للطباعة
      const shouldPrint = !(printAfterSaving === 0 || String(printAfterSaving) === "0");

      if (shouldPrint && printingMethod === "automatic") {
        try {
          await handleBrowserPrintAfterEnd({
            order: orderDetails,
            items: orderDetails?.items || [],
            invoiceSettings,
          });
        } catch (errPrint) {
          console.warn("⚠️ فشل في طباعة الفاتورة:", errPrint);
        }
      } else {
        // حالات عدم الطباعة توضيحية للـ logs
        if (!shouldPrint) {
          console.log("ℹ️ خيار print_invoice_after_saving معطل - لن تتم الطباعة بعد الحفظ");
        } else {
          console.log("ℹ️ طريقة الطباعة ليست automatic - لا يتم فتح نافذة الطباعة بعد الحفظ");
        }
      }

      // لا تغلق الموديل تلقائياً
      alert("✅ تم إنشاء الفاتورة: " + response?.message);
      if (onSuccess) onSuccess();
    } catch (err) {
      alert("❌ فشل في إنشاء الفاتورة: " + err.message);
    }
  };

  // Modal state for multi-payment
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // ✅ تحديث handleOpenPaymentModal لقبول مبلغ اختياري
  const handleOpenPaymentModal = (defaultAmount = null) => {
    setPayments([
      {
        bank_id: "",
        amount: defaultAmount !== null ? defaultAmount : invoiceSettings.total_amount,
        payment_method: "cash",
      },
    ]);
    setPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => setPaymentModalOpen(false);

  const handleMultiPaymentSubmit = async () => {
    // تحقق من اختيار الخزنة لكل دفعة
    for (let i = 0; i < payments.length; i++) {
      if (!payments[i].bank_id) {
        alert("يرجى اختيار الخزنة لكل طريقة دفع.");
        return;
      }
    }
    // تحقق من أن مجموع المبالغ لا يتجاوز الإجمالي النهائي
    const totalPaid = payments.reduce(
      (acc, p) => acc + (parseFloat(p.amount) || 0),
      0
    );
    const invoiceTotal = parseFloat(invoiceSettings.total_amount) || 0;

    if (totalPaid > invoiceTotal) {
      alert("إجمالي المبالغ المدفوعة أكبر من إجمالي الفاتورة.");
      return;
    }

    // استدعاء handleCreateInvoice مباشرة (بدون غلق الموديل)
    await handleCreateInvoice();
    // NEW: تأجيل غلق الموديل حتى ينتهي المستخدم من الطباعة
    // setPaymentModalOpen(false);
  };

  // Modal states for service, discount, tax
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [taxModalOpen, setTaxModalOpen] = useState(false);

  // Temporary values for modals
  const [serviceTemp, setServiceTemp] = useState({ value: "", ratio: "" });
  const [discountTemp, setDiscountTemp] = useState({ value: "", ratio: "" });
  const [taxTemp, setTaxTemp] = useState({ value: "", ratio: "" });

  // Handlers for opening modals
  const handleOpenServiceModal = () => {
    if (serviceLocked) {
      alert("⚠️ لا يمكن تعديل الخدمة لأنها إلزامية.");
      return;
    }
    setServiceTemp({
      value: invoiceSettings.service,
      ratio: invoiceSettings.service_ratio,
    });
    setServiceModalOpen(true);
  };

  const handleOpenTaxModal = () => {
    if (taxLocked) {
      alert("⚠️ لا يمكن تعديل الضريبة لأنها إلزامية.");
      return;
    }
    setTaxTemp({
      value: invoiceSettings.tax,
      ratio: invoiceSettings.tax_ratio,
    });
    setTaxModalOpen(true);
  };

  const handleOpenDiscountModal = () => {
    if (discountLocked) {
      alert("⚠️ لا يمكن تعديل الخصم لأنه إلزامي.");
      return;
    }
    setDiscountTemp({
      value: invoiceSettings.discount,
      ratio: invoiceSettings.discount_ratio,
    });
    setDiscountModalOpen(true);
  };

  // Handlers for saving modal values
  const handleSaveService = () => {
    const ratio = parseFloat(serviceTemp.ratio) || 0;
    const value = parseFloat(serviceTemp.value) || 0;

    const updated = {
      ...invoiceSettings,
      service: value,
      service_ratio: ratio,
    };
    
    // ✅ استخدم calculateTotalAmount من useInvoice
    updated.total_amount = calculateTotalAmount(updated);
    updateInvoiceSettings(updated);
    setServiceModalOpen(false);
  };

  const handleSaveDiscount = () => {
    const ratio = parseFloat(discountTemp.ratio) || 0;
    const value = parseFloat(discountTemp.value) || 0;

    const updated = {
      ...invoiceSettings,
      discount: value,
      discount_ratio: ratio,
    };
    
    // ✅ استخدم calculateTotalAmount من useInvoice
    updated.total_amount = calculateTotalAmount(updated);
    updateInvoiceSettings(updated);
    setDiscountModalOpen(false);
  };

  const handleSaveTax = () => {
    const ratio = parseFloat(taxTemp.ratio) || 0;
    const value = parseFloat(taxTemp.value) || 0;

    const updated = { ...invoiceSettings };

    // ✅ حفظ القيم كما هي، calculateTotalAmount سيتعامل معها
    if (ratio > 0) {
      updated.tax_is_percentage = true;
      updated.tax_ratio = ratio;
      updated.tax = 0;
    } else if (value > 0) {
      updated.tax_is_percentage = false;
      updated.tax = value;
      updated.tax_ratio = 0;
    } else {
      updated.tax_is_percentage = false;
      updated.tax = 0;
      updated.tax_ratio = 0;
    }

    // ✅ استخدم calculateTotalAmount من useInvoice
    updated.total_amount = calculateTotalAmount(updated);
    updateInvoiceSettings(updated);
    setTaxModalOpen(false);
  };

  // State for unified add menu
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  const handleOpenAddMenu = (event) => setAddMenuAnchor(event.currentTarget);
  const handleCloseAddMenu = () => setAddMenuAnchor(null);

  const handleAddMenuClick = (type) => {
    handleCloseAddMenu();
    if (type === "service") handleOpenServiceModal();
    if (type === "discount") handleOpenDiscountModal();
    if (type === "tax") handleOpenTaxModal();
  };

  // Printer selection state
  const [printerModalOpen, setPrinterModalOpen] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(
    localStorage.getItem("selected_printer") || ""
  );

  // Use cashiers as printers list
  const printers = cashiers;

  const handleOpenPrinterModal = () => setPrinterModalOpen(true);
  const handleClosePrinterModal = () => setPrinterModalOpen(false);

  const handleSelectPrinter = (printerId) => {
    setSelectedPrinter(printerId);
    localStorage.setItem("selected_printer", printerId);
    setPrinterModalOpen(false);
  };

  // ✅ دالة الطباعة المستخرجة للاستخدام مع الاختصار
  const handlePrint = async () => {
    try {
      // التحقق من تحميل الإعدادات
      if (settingsLoading) {
        alert("⚠️ جاري تحميل إعدادات الطباعة، حاول مرة أخرى بعد قليل.");
        return;
      }

      let currentOrder = orderDetails;
      if (!currentOrder || currentOrder.id !== orderId) {
        await loadOrder(orderId);
        currentOrder = orderDetails;
      }

      if (!currentOrder) {
        alert("❌ لا توجد بيانات لهذا الطلب");
        return;
      }

      // جلب عدد مرات الطباعة للأوردر
      let orderPrintNumber = 1;
      try {
        const countData = await getCountPrintOrder(orderId);
        if (countData?.data?.order_print_number) {
          orderPrintNumber = countData.data.order_print_number;
        }
      } catch (errCount) {
        console.warn("⚠️ فشل في جلب عدد الطباعات، استخدام قيمة افتراضية:", errCount);
      }

      // بناء بيانات الفاتورة
      const invoiceData = {
        sub_total: parseFloat(invoiceSettings.sub_total || 0),
        service: parseFloat(invoiceSettings.service || 0),
        service_ratio: parseFloat(invoiceSettings.service_ratio || 0),
        tax: parseFloat(invoiceSettings.tax || 0),
        tax_ratio: parseFloat(invoiceSettings.tax_ratio || 0),
        discount: parseFloat(invoiceSettings.discount || 0),
        discount_ratio: parseFloat(invoiceSettings.discount_ratio || 0),
        delivery: parseFloat(invoiceSettings.delivery || 0),
        delivery_ratio: parseFloat(invoiceSettings.delivery_ratio || 0),
        total_amount: parseFloat(invoiceSettings.total_amount || 0),
        order_print_number: orderPrintNumber,
      };

      const method = (settings?.printing_method || "").toString().toLowerCase();

      if (method === "automatic") {
        handleBrowserPrint(currentOrder, invoiceData, orderId);
        return;
      }

      if (method === "manual") {
        await printCustomerInvoice({
          order_id: orderId,
          order_print_number: invoiceData.order_print_number,
          tax: invoiceData.tax || invoiceData.tax_ratio || 0,
          service: invoiceData.service || invoiceData.service_ratio || 0,
          discount: invoiceData.discount || invoiceData.discount_ratio || 0,
        });
        return;
      }

      alert("⚠️ طريقة الطباعة غير معروفة: " + (settings?.printing_method || "غير محدد"));
    } catch (err) {
      console.error("❌ فشل في الطباعة:", err);
      alert("حدث خطأ أثناء الطباعة");
    }
  };

  // ✅ useEffect لإضافة اختصار Ctrl + P
  useEffect(() => {
    const handleKeyDown = (e) => {
      // التحقق من Ctrl + P (أو Cmd + P على Mac)
      // استخدام keyCode بدلاً من key للعمل مع جميع لغات لوحة المفاتيح
      // keyCode 80 = حرف P
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'ح' || e.keyCode === 80)) {
        e.preventDefault(); // منع السلوك الافتراضي للطباعة من المتصفح
        if (hasPermission("orders_tables_print_table")) {
          handlePrint();
        }
      }
    };

    // إضافة المستمع
    window.addEventListener('keydown', handleKeyDown);

    // تنظيف المستمع عند unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [orderId, orderDetails, invoiceSettings, settingsLoading, settings, hasPermission]);

  // ✅ useEffect لإضافة اختصار F11 لزر الدفع
  useEffect(() => {
    const handleKeyDown = (e) => {
      // التحقق من F11 (keyCode 122)
      if (e.key === 'F11' || e.keyCode === 122) {
        e.preventDefault(); // منع السلوك الافتراضي لـ F11
        
        // ✅ تحقق من printing_method
        const printingMethod = (settings?.printing_method || "").toString().toLowerCase();
        const isAutomatic = printingMethod === "automatic";

        // ✅ الحصول على الإجمالي النهائي
        const totalAmount = parseFloat(invoiceSettings.total_amount || 0);

        // إذا كانت الطباعة automatic، لا تطلب اختيار طابعة
        if (isAutomatic) {
          handleOpenPaymentModal(totalAmount);
          return;
        }

        // إذا كانت manual، تحقق من اختيار الطابعة
        if (!selectedPrinter) {
          if (
            window.confirm(
              "يرجى اختيار الطابعة أولاً. هل تريد المتابعة بدون اختيار طابعة؟"
            )
          ) {
            handleOpenPaymentModal(totalAmount);
          }
          return;
        }
        handleOpenPaymentModal(totalAmount);
      }
    };

    // إضافة المستمع
    window.addEventListener('keydown', handleKeyDown);

    // تنظيف المستمع عند unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedPrinter, settings, invoiceSettings.total_amount]);

  return (
    <Paper
      sx={{
        p: { xs: 0.75, sm: 1.5, md: 2.5 },
        mt: { xs: 0.5, sm: 1.5 },
        borderRadius: 3,
        width: "100%",
        maxWidth: { xs: "99vw", sm: "min(92vw,420px)", md: "min(420px,50vw)" },
        margin: "auto",
        boxShadow: 4,
        bgcolor: isDark ? "#232334" : "#fff",
        color: isDark ? "#fff" : "#222",
        transition: "background 0.2s,color 0.2s",
      }}
    >
      <Divider sx={{ mb: 2, bgcolor: isDark ? "#444" : "#ccc" }} />
      {/* زر اختيار الطابعة وزر إضافة خدمة/خصم/ضريبة */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        {/* زر اختيار الطابعة وزر الطباعة */}
        {hasPermission("orders_tables_print_table") && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOpenPrinterModal}
              sx={{
                borderRadius: "50%",
                minWidth: 0,
                width: 34,
                height: 34,
                boxShadow: 1,
                bgcolor: isDark ? "#232334" : "#f5f5f5",
                color: "#1976d2",
                border: "1.5px solid #1976d2",
                "&:hover": {
                  bgcolor: isDark ? "#232334" : "#e3e3e3",
                  color: "#1565c0",
                  borderColor: "#1565c0",
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0,
                transition: "all 0.15s",
              }}
              aria-label="اختيار الطابعة"
              title={
                selectedPrinter
                  ? printers.find((p) => p.id?.toString() === selectedPrinter)
                      ?.name || "اختيار الطابعة"
                  : "اختيار الطابعة"
              }
            >
              <PrintIcon sx={{ fontSize: 18 }} />
            </Button>
            {/* زر الطباعة بجوار الأيقونة */}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PrintIcon sx={{ fontSize: isTablet ? 20 : 18 }} />}
              onClick={handlePrint}
              title="طباعة (Ctrl+P)"
              size={controlSize}
            >
              طباعة
            </Button>
          </Box>
        )}

        {/* زر إضافة خدمة/خصم/ضريبة */}
        <Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenAddMenu}
            sx={{
              borderRadius: "50%",
              minWidth: 0,
              width: 34,
              height: 34,
              boxShadow: 1,
              bgcolor: isDark ? "#232334" : "#f5f5f5",
              color: "#1976d2",
              border: "1.5px solid #1976d2",
              "&:hover": {
                bgcolor: isDark ? "#232334" : "#e3e3e3",
                color: "#1565c0",
                borderColor: "#1565c0",
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0,
              transition: "all 0.15s",
            }}
            aria-label="إضافة"
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </Button>
          <Menu
            anchorEl={addMenuAnchor}
            open={Boolean(addMenuAnchor)}
            onClose={handleCloseAddMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{
              "& .MuiMenu-paper": {
                borderRadius: 2,
                minWidth: 170,
                boxShadow: 3,
              },
            }}
          >
            {hasPermission("orders_other_edite_service") && (
              <MenuItem
                onClick={() => handleAddMenuClick("service")}
                sx={{
                  fontWeight: 500,
                  color: "#1976d2",
                  "& svg": { mr: 1 },
                }}
                disabled={serviceLocked}
              >
                <AddCircleIcon sx={{ color: "#1976d2" }} fontSize="small" />
                إضافة خدمة
              </MenuItem>
            )}
            {hasPermission("orders_other_edite_discount") && (
              <MenuItem
                onClick={() => handleAddMenuClick("discount")}
                sx={{
                  fontWeight: 500,
                  color: "#388e3c",
                  "& svg": { mr: 1 },
                }}
                disabled={discountLocked}
              >
                <AddCircleIcon sx={{ color: "#388e3c" }} fontSize="small" />
                إضافة خصم
              </MenuItem>
            )}
            {hasPermission("orders_other_edite_tax") && (
              <MenuItem
                onClick={() => handleAddMenuClick("tax")}
                sx={{
                  fontWeight: 500,
                  color: "#d32f2f",
                  "& svg": { mr: 1 },
                }}
                disabled={taxLocked}
              >
                <AddCircleIcon sx={{ color: "#d32f2f" }} fontSize="small" />
                إضافة ضريبة
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>
      <Box component="form" noValidate autoComplete="off">
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {/* احذف الدروب داون الخاص بالطابعة من HeaderFields */}
        </Grid>
      </Box>
      <Box component="form" noValidate autoComplete="off">
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12}>
            <Box
              sx={{
                bgcolor: isDark ? "#232334" : "#f9f9f9",
                borderRadius: 2,
                p: 2,
                mb: 2,
                border: `1px dashed ${isDark ? "#444" : "#ccc"}`,
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ color: "#aaa" }}>الإجمالي</Typography>
                  <Typography sx={{ color: "#fff" }}>
                    {parseFloat(invoiceSettings.sub_total || 0).toFixed(2)}
                  </Typography>
                </Stack>

                {(invoiceSettings.service || invoiceSettings.service_ratio) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#1976d2" }}>
                      الخدمة{" "}
                      {invoiceSettings.service_ratio
                        ? ` ${invoiceSettings.service_ratio}%`
                        : ""}
                    </Typography>
                    <Typography sx={{ color: "#1976d2" }}>
                      {(() => {
                        const val = parseFloat(invoiceSettings.service || 0);
                        const ratio = parseFloat(invoiceSettings.service_ratio || 0);
                        const sub = parseFloat(invoiceSettings.sub_total || 0);
                        return (val + (ratio / 100) * sub).toFixed(2);
                      })()}
                    </Typography>
                  </Stack>
                )}

                {(invoiceSettings.discount || invoiceSettings.discount_ratio) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#388e3c" }}>
                      الخصم{" "}
                      {invoiceSettings.discount_ratio
                        ? ` ${invoiceSettings.discount_ratio}%`
                        : ""}
                    </Typography>
                    <Typography sx={{ color: "#388e3c" }}>
                      -
                      {(() => {
                        const val = parseFloat(invoiceSettings.discount || 0);
                        const ratio = parseFloat(invoiceSettings.discount_ratio || 0);
                        const sub = parseFloat(invoiceSettings.sub_total || 0);
                        return (val + (ratio / 100) * sub).toFixed(2);
                      })()}
                    </Typography>
                  </Stack>
                )}

                {(invoiceSettings.tax || invoiceSettings.tax_ratio) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#d32f2f" }}>
                      الضريبة{" "}
                      {invoiceSettings.tax_is_percentage && invoiceSettings.tax_ratio
                        ? ` ${invoiceSettings.tax_ratio}%`
                        : invoiceSettings.tax_ratio
                        ? ` ${invoiceSettings.tax_ratio}%`
                        : ""}
                    </Typography>
                    <Typography sx={{ color: "#d32f2f" }}>
                      {(() => {
                        // ✅ عرض الضريبة بناءً على نوعها
                        if (invoiceSettings.tax_is_percentage && invoiceSettings.tax_ratio) {
                          const ratio = parseFloat(invoiceSettings.tax_ratio || 0);
                          const sub = parseFloat(invoiceSettings.sub_total || 0);
                          return ((ratio / 100) * sub).toFixed(2);
                        } else if (invoiceSettings.tax_ratio) {
                          const ratio = parseFloat(invoiceSettings.tax_ratio || 0);
                          const sub = parseFloat(invoiceSettings.sub_total || 0);
                          return ((ratio / 100) * sub).toFixed(2);
                        } else {
                          return parseFloat(invoiceSettings.tax || 0).toFixed(2);
                        }
                      })()}
                    </Typography>
                  </Stack>
                )}

                {(invoiceSettings.delivery || invoiceSettings.delivery_ratio) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#9c27b0" }}>
                      التوصيل{" "}
                      {invoiceSettings.delivery_ratio
                        ? ` ${invoiceSettings.delivery_ratio}%`
                        : ""}
                    </Typography>
                    <Typography sx={{ color: "#9c27b0" }}>
                      {(() => {
                        const val = parseFloat(invoiceSettings.delivery || 0);
                        const ratio = parseFloat(invoiceSettings.delivery_ratio || 0);
                        const sub = parseFloat(invoiceSettings.sub_total || 0);
                        return (val + (ratio / 100) * sub).toFixed(2);
                      })()}
                    </Typography>
                  </Stack>
                )}

                <Divider
                  sx={{
                    borderStyle: "dashed",
                    my: 1,
                    bgcolor: isDark ? "#444" : "#ccc",
                  }}
                />
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight="bold">الإجمالي النهائي</Typography>
                  <Typography fontWeight="bold">
                    {parseFloat(invoiceSettings.total_amount || 0).toFixed(2)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1, bgcolor: isDark ? "#444" : "#ccc" }} />
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                // ✅ تحقق من printing_method
                const printingMethod = (settings?.printing_method || "").toString().toLowerCase();
                const isAutomatic = printingMethod === "automatic";

                // إذا كانت الطباعة automatic، لا تطلب اختيار طابعة
                if (isAutomatic) {
                  handleOpenPaymentModal();
                  return;
                }

                // إذا كانت manual، تحقق من اختيار الطابعة
                if (!selectedPrinter) {
                  if (
                    window.confirm(
                      "يرجى اختيار الطابعة أولاً. هل تريد المتابعة بدون اختيار طابعة؟"
                    )
                  ) {
                    handleOpenPaymentModal();
                  }
                  return;
                }
                handleOpenPaymentModal();
              }}
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
                py: { xs: 0.9, sm: 1.2 },
                mt: 0,
                borderRadius: 2,
                boxShadow: 2,
                bgcolor: "#fbc2eb",
                color: "#222",
                "&:hover": {
                  bgcolor: "#ff9a9e",
                },
              }}
              title="دفع (F11)"
            >
              دفع
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Modal for Multi Payment */}
      <PaymentDialog
        open={paymentModalOpen}
        onClose={handleClosePaymentModal}
        payments={payments}
        setPayments={setPayments}
        banks={banks}
        totalAmount={invoiceSettings.total_amount} // ✅ بدل form.total_amount
        onSubmit={handleMultiPaymentSubmit}
        isDark={isDark}
        handleCreateInvoice={handleCreateInvoice}
      />

      {/* Modal for Service */}
      <Dialog
        open={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDark ? "#232334" : "#fff",
            color: isDark ? "#fff" : "#222",
            width: { xs: "94vw", sm: "min(420px,90vw)" },
          },
        }}
      >
        <DialogTitle>إضافة خدمة</DialogTitle>
        <DialogContent>
          <TextField
            label="قيمة الخدمة"
            type="number"
            fullWidth
            margin="normal"
            value={serviceTemp.value}
            onChange={(e) =>
              setServiceTemp((s) => ({ ...s, value: e.target.value }))
            }
          />
          <TextField
            label="نسبة الخدمة (%)"
            type="number"
            fullWidth
            margin="normal"
            value={serviceTemp.ratio}
            onChange={(e) =>
              setServiceTemp((s) => ({ ...s, ratio: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceModalOpen(false)} color="secondary">
            إلغاء
          </Button>
          <Button
            onClick={handleSaveService}
            variant="contained"
            color="primary"
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal for Discount */}
      <Dialog
        open={discountModalOpen}
        onClose={() => setDiscountModalOpen(false)}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDark ? "#232334" : "#fff",
            color: isDark ? "#fff" : "#222",
            width: { xs: "94vw", sm: "min(420px,90vw)" },
          },
        }}
      >
        <DialogTitle>إضافة خصم</DialogTitle>
        <DialogContent>
          <TextField
            label="قيمة الخصم"
            type="number"
            fullWidth
            margin="normal"
            value={discountTemp.value}
            onChange={(e) =>
              setDiscountTemp((s) => ({ ...s, value: e.target.value }))
            }
          />
          <TextField
            label="نسبة الخصم (%)"
            type="number"
            fullWidth
            margin="normal"
            value={discountTemp.ratio}
            onChange={(e) =>
              setDiscountTemp((s) => ({ ...s, ratio: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountModalOpen(false)} color="secondary">
            إلغاء
          </Button>
          <Button
            onClick={handleSaveDiscount}
            variant="contained"
            color="primary"
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal for Tax */}
      <Dialog
        open={taxModalOpen}
        onClose={() => setTaxModalOpen(false)}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDark ? "#232334" : "#fff",
            color: isDark ? "#fff" : "#222",
            width: { xs: "94vw", sm: "min(420px,90vw)" },
          },
        }}
      >
        <DialogTitle>إضافة ضريبة</DialogTitle>
        <DialogContent>
          <TextField
            label="قيمة الضريبة"
            type="number"
            fullWidth
            margin="normal"
            value={taxTemp.value}
            onChange={(e) =>
              setTaxTemp((s) => ({ ...s, value: e.target.value }))
            }
          />
          <TextField
            label="نسبة الضريبة (%)"
            type="number"
            fullWidth
            margin="normal"
            value={taxTemp.ratio}
            onChange={(e) =>
              setTaxTemp((s) => ({ ...s, ratio: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxModalOpen(false)} color="secondary">
            إلغاء
          </Button>
          <Button onClick={handleSaveTax} variant="contained" color="primary">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal for Printer Selection */}
      <Dialog
        open={printerModalOpen}
        onClose={handleClosePrinterModal}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: isDark ? "#232334" : "#fff",
            color: isDark ? "#fff" : "#222",
            width: { xs: "94vw", sm: "min(420px,90vw)" },
          },
        }}
      >
        <DialogTitle>اختر الطابعة</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {printers.map((printer) => (
              <Button
                key={printer.id}
                variant={
                  selectedPrinter === printer.id?.toString()
                    ? "contained"
                    : "outlined"
                }
                color="primary"
                fullWidth
                sx={{
                  mb: 1,
                  fontWeight:
                    selectedPrinter === printer.id?.toString()
                      ? "bold"
                      : "normal",
                  bgcolor:
                    selectedPrinter === printer.id?.toString()
                      ? "#1976d2"
                      : undefined,
                  color:
                    selectedPrinter === printer.id?.toString()
                      ? "#fff"
                      : undefined,
                  borderColor: "#1976d2",
                  "&:hover": {
                    bgcolor: "#1565c0",
                    color: "#fff",
                  },
                }}
                onClick={() => handleSelectPrinter(printer.id?.toString())}
              >
                {printer.name}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrinterModal} color="secondary">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default InvoiceCreator;